import { RegularExpressions } from 'utils/regularExpressions';

const Errors = {
  isRequired: 'This field is required',
  tooShort: 'At least two characters',
  noSpecialCharacters: `Special characters allowed: "space", "dot (.)", "comma (,)", "apostrophe (')", "hyphen (-)", "ampersand (&)"`,
  website: 'Incorrect format. Example: "www.example.com" or "https://example.com"',
  email: {
    wrongFormat: 'Incorrect format. Example: "yourCorporate@email.com"',
    corporateEmail: 'Enter a corporate email',
  },
  phone: 'Incorrect format. Example: +380631122333, +4900000000000',
  dateOfIncident: 'Incorrect format. Example: 2022-10-15 (YYYY-MM-DD)',
  files: 'Up to 3 files allowed',
};

export const useValidation = () => {
  const isEmpty = (valueToCheck: string) => {
    return !valueToCheck.length;
  };

  const isReasonsMultipleValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (!formData[key].length) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    return true;
  };

  // Validation for non-user names (company names etc.):
  const isNameValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (!RegularExpressions.noSpecialCharactersLight.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.noSpecialCharacters,
        };
      });
      return false;
    }

    return true;
  };

  // Validation for user names (first or last names):
  const isUserNameValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (formData[key].length < 2) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.tooShort,
        };
      });
      return false;
    }

    if (!RegularExpressions.noSpecialCharacters.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.noSpecialCharacters,
        };
      });
      return false;
    }

    return true;
  };

  const isWebsiteValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (!RegularExpressions.website.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.website,
        };
      });
      return false;
    }

    return true;
  };

  const isEmailValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (!RegularExpressions.email.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.email.wrongFormat,
        };
      });
      return false;
    }

    return true;
  };

  const isEmailNotBlacklisted = ({ formData, key, setFormErrors }: any): boolean => {
    const domain: string = formData[key].split('@')[1];
    const domainBlacklist: string[] = [
      'gmail.com',
      'gmx.com',
      'googlemail.com',
      'outlook.com',
      'icloud.com',
      'mail.com',
      'yahoo.com',
      'hotmail.com',
      'live.com',
      'mail.ru',
      'web.de',
      'hotmail.co.uk',
      'gmailx.com',
    ];
    if (domainBlacklist.some((item: string) => item === domain)) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.email.corporateEmail,
        };
      });
      return false;
    }
    return true;
  };

  const isPhoneNumberValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (!RegularExpressions.phoneNumber.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.phone,
        };
      });
      return false;
    }

    return true;
  };

  const isCountryValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    return true;
  };

  const isDateOfIncidentValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    if (!RegularExpressions.dateOfIncident.test(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.dateOfIncident,
        };
      });
      return false;
    }

    return true;
  };

  const isReasonValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    return true;
  };

  const isManagerValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    return true;
  };

  const isTextareaValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (isEmpty(formData[key])) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.isRequired,
        };
      });
      return false;
    }

    return true;
  };

  const isFilesValid = ({ formData, key, setFormErrors }: any): boolean => {
    if (formData[key].length > 3) {
      setFormErrors((state: any) => {
        return {
          ...state,
          [key]: Errors.files,
        };
      });
      return false;
    }

    return true;
  };

  return {
    isNameValid,
    isWebsiteValid,
    isUserNameValid,
    isEmailValid,
    isEmailNotBlacklisted,
    isPhoneNumberValid,
    isCountryValid,
    isDateOfIncidentValid,
    isReasonValid,
    isManagerValid,
    isTextareaValid,
    isFilesValid,
    isReasonsMultipleValid,
  };
};
